// Liquid projects the price. This extra read checks codes that are saved but
// not yet applicable (for example, a free-cologne reward awaiting its item).
export function verifiedShippingCart(cart, expected) {
  if (!cart || cart.currency !== 'USD' || !Array.isArray(cart.discount_codes)) return false;
  if (cart.total_price !== expected.total || cart.original_total_price !== expected.original) return false;
  if (cart.total_price <= 0 || cart.total_price >= 8000) return false;
  if (cart.discount_codes.length === 0) return expected.percent === 100 && cart.total_discount === 0;
  return expected.percent === 85 && cart.discount_codes.length === 1 &&
    String(cart.discount_codes[0].code).toUpperCase() === 'DZ15' &&
    cart.discount_codes[0].applicable === true;
}

if (typeof customElements !== 'undefined' && !customElements.get('dz-shipping-offer')) {
  class ShippingOffer extends HTMLElement {
    static observedAttributes = ['data-cart-total', 'data-cart-original', 'data-percent', 'hidden'];
    #controller;
    #submitting = false;
    #generation = 0;
    #settingHidden = false;
    connectedCallback() {
      this.addEventListener('submit', this.#submit);
      document.addEventListener('submit', this.#discountInput, true);
      document.addEventListener('input', this.#discountInput, true);
      document.addEventListener('cart:update', this.#verify);
      document.addEventListener('discount:update', this.#verify);
      window.addEventListener('pageshow', this.#restore);
      this.#verify();
    }
    disconnectedCallback() {
      this.#generation++;
      this.#controller?.abort();
      this.removeEventListener('submit', this.#submit);
      document.removeEventListener('submit', this.#discountInput, true);
      document.removeEventListener('input', this.#discountInput, true);
      document.removeEventListener('cart:update', this.#verify);
      document.removeEventListener('discount:update', this.#verify);
      window.removeEventListener('pageshow', this.#restore);
    }
    attributeChangedCallback(name) {
      if (!this.isConnected || this.#settingHidden) return;
      // Horizon may reapply the server-rendered hidden attribute without
      // reconnecting this element or changing the cart totals.
      if (name !== 'hidden' || this.hidden) this.#verify();
    }
    #setHidden(value) {
      this.#settingHidden = true;
      this.hidden = value;
      this.#settingHidden = false;
    }
    #restore = (event) => {
      if (!event.persisted) return;
      this.#submitting = false;
      const button = this.querySelector('button');
      if (button) button.disabled = false;
      this.#verify();
    };
    #expected() {
      return { total: Number(this.dataset.cartTotal), original: Number(this.dataset.cartOriginal), percent: Number(this.dataset.percent) };
    }
    async #read(signal) {
      const response = await fetch(this.dataset.cartUrl, { credentials: 'same-origin', cache: 'no-store', signal });
      if (!response.ok) throw new Error('Cart check unavailable');
      return response.json();
    }
    #discountInput = (event) => {
      if (event.target instanceof Element && event.target.closest('.cart-discount__form')) {
        this.#generation++;
        this.#setHidden(true);
        this.#controller?.abort();
      }
    };
    #verify = async () => {
      this.#generation++;
      this.#setHidden(true);
      this.#controller?.abort();
      const controller = this.#controller = new AbortController();
      try {
        const cart = await this.#read(controller.signal);
        if (this.isConnected && !controller.signal.aborted && this.#controller === controller) this.#setHidden(!verifiedShippingCart(cart, this.#expected()));
      } catch { /* Leave the offer hidden; ordinary checkout remains available. */ }
    };
    #submit = async (event) => {
      event.preventDefault();
      if (this.#submitting || this.hidden || !(event.target instanceof HTMLFormElement)) return;
      this.#submitting = true;
      const form = event.target;
      const expected = this.#expected();
      const generation = this.#generation;
      const button = form.querySelector('button');
      button.disabled = true;
      try {
        const cart = await this.#read();
        if (!this.isConnected || !form.isConnected || this.hidden || generation !== this.#generation) {
          button.disabled = false;
          this.#submitting = false;
          return;
        }
        if (verifiedShippingCart(cart, expected)) {
          // Horizon morphing can clear hidden-input values. Keep the offer
          // explicitly one unit and preserve the locale-aware cart return.
          form.elements.namedItem('quantity').value = '1';
          form.elements.namedItem('return_to').value = this.dataset.cartUrl.replace(/\.js$/, '');
          // Exactly one normal add request. Do not retry after a failed write.
          HTMLFormElement.prototype.submit.call(form);
        } else {
          window.location.reload();
        }
      } catch {
        const error = this.querySelector('[data-offer-error]');
        error.hidden = false;
        button.disabled = false;
        this.#submitting = false;
      }
    };
  }
  customElements.define('dz-shipping-offer', ShippingOffer);
}
